import type { TypedSupabaseClient } from '../client';
import type {
  CreateProClientInput,
  IProClientRepository,
  ProClient,
  ProClientFilters,
  UpdateProClientInput,
} from '../../../interfaces/src';

export class SupabaseProClientRepository implements IProClientRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(filters?: ProClientFilters): Promise<ProClient[]> {
    let query = (this.supabase as any).from('pro_clients').select('*');
    if (filters?.search) {
      const term = filters.search.replace(/%/g, '');
      query = query.or(
        `nom.ilike.%${term}%,prenom.ilike.%${term}%,telephone.ilike.%${term}%,email.ilike.%${term}%`,
      );
    }
    const { data, error } = await query.order('nom');
    if (error) throw error;
    return ((data ?? []) as Array<Record<string, unknown>>).map(mapClient);
  }

  async getById(id: string): Promise<ProClient | null> {
    const { data, error } = await (this.supabase as any)
      .from('pro_clients')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data ? mapClient(data) : null;
  }

  async create(input: CreateProClientInput): Promise<ProClient> {
    const currentPro = await currentProCustomerContext(this.supabase);
    const { data, error } = await (this.supabase as any)
      .from('pro_clients')
      .insert({
        tenant_id: currentPro.tenantId,
        pro_customer_id: currentPro.id,
        nom: input.nom,
        prenom: input.prenom ?? null,
        email: input.email ?? null,
        telephone: input.telephone ?? null,
        adresse: input.adresse ?? null,
        ville: input.ville ?? null,
        cp: input.cp ?? null,
        complement: input.complement ?? null,
        notes: input.notes ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    return mapClient(data);
  }

  async update(id: string, input: UpdateProClientInput): Promise<ProClient> {
    const patch: Record<string, unknown> = {};
    if (input.nom !== undefined) patch['nom'] = input.nom;
    if (input.prenom !== undefined) patch['prenom'] = input.prenom;
    if (input.email !== undefined) patch['email'] = input.email;
    if (input.telephone !== undefined) patch['telephone'] = input.telephone;
    if (input.adresse !== undefined) patch['adresse'] = input.adresse;
    if (input.ville !== undefined) patch['ville'] = input.ville;
    if (input.cp !== undefined) patch['cp'] = input.cp;
    if (input.complement !== undefined) patch['complement'] = input.complement;
    if (input.notes !== undefined) patch['notes'] = input.notes;
    const { data, error } = await (this.supabase as any)
      .from('pro_clients')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapClient(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await (this.supabase as any).from('pro_clients').delete().eq('id', id);
    if (error) throw error;
  }
}

async function currentProCustomerContext(
  client: TypedSupabaseClient,
): Promise<{ id: string; tenantId: string }> {
  const { data: userData } = await client.auth.getUser();
  const userId = userData.user?.id;
  if (!userId) throw new Error('Not authenticated');
  const { data, error } = await (client as any)
    .from('pro_customers')
    .select('id, tenant_id')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Aucun profil pro');
  const id = data['id'] as string | undefined;
  const tenantId = data['tenant_id'] as string | undefined;
  if (!id || !tenantId) throw new Error('Profil pro incomplet');
  return { id, tenantId };
}

function mapClient(row: Record<string, unknown>): ProClient {
  return {
    id: row['id'] as string,
    tenantId: row['tenant_id'] as string,
    proCustomerId: row['pro_customer_id'] as string,
    nom: row['nom'] as string,
    prenom: (row['prenom'] as string) ?? null,
    email: (row['email'] as string) ?? null,
    telephone: (row['telephone'] as string) ?? null,
    adresse: (row['adresse'] as string) ?? null,
    ville: (row['ville'] as string) ?? null,
    cp: (row['cp'] as string) ?? null,
    complement: (row['complement'] as string) ?? null,
    depense: Number(row['depense'] ?? 0),
    commission: Number(row['commission'] ?? 0),
    soldeCumule: Number(row['solde_cumule'] ?? 0),
    notes: (row['notes'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}
